import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  Separator,
  Text,
  TextField,
  Section,
} from "@radix-ui/themes";
import { Plus } from "lucide-react";
import { useExploreApi } from "@/v2/entities/explore/Explore.api";
import { ExploreView } from "@/v2/entities/explore/Explore.model";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { ExploreViewsDisplayOptions } from "@/v2/features/explore/ExploreViewsDisplayOptions";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import AnalyticsUpIcon from "@/v2/assets/icons/AanalyticsUpIcon";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";

type ViewColumn = "title" | "source" | "createdAt" | "updatedAt";

export function ExploreViews() {
  const navigate = useNavigate();
  const { getViews } = useExploreApi();
  const [search, setSearch] = useState("");
  const [visibleColumns, setVisibleColumns] = useState<ViewColumn[]>([
    "title",
    "source",
    "createdAt",
    "updatedAt",
  ]);
  const [sorting, _] = useState<SortingState>([
    { id: "updatedAt", desc: true },
  ]);

  // Fetch all views
  const { data: views, isLoading: viewsLoading } = useQuery({
    queryKey: ["explore-views"],
    queryFn: () => getViews(),
  });

  const handleCreateView = useCallback(() => {
    navigate("/explore/views/new");
  }, [navigate]);

  const getSourceIcon = useCallback((source: string) => {
    if (source === "conversations") {
      return <Comment01Icon fill="orange" />;
    }
    return <Ticket03Icon fill="blue" />;
  }, []);

  const sortedAndFilteredViews = useMemo(() => {
    if (!views) return [];

    let filteredViews = views.filter((view) =>
      view.title.toLowerCase().includes(search.toLowerCase())
    );

    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      filteredViews = [...filteredViews].sort((a, b) => {
        const aValue = a[id as keyof ExploreView];
        const bValue = b[id as keyof ExploreView];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return desc ? 1 : -1;
        if (bValue == null) return desc ? -1 : 1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return desc
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (aValue < bValue) return desc ? 1 : -1;
        if (aValue > bValue) return desc ? -1 : 1;
        return 0;
      });
    }

    return filteredViews;
  }, [views, search, sorting]);

  const tableColumns = useMemo(() => {
    const allColumns: ColumnDef<ExploreView>[] = [
      {
        id: "title",
        header: "Title",
        accessorKey: "title",
        cell: ({ row }) => {
          return (
            <Text size={"2"} color="gray" weight="medium" highContrast>
              {row.original.title}
            </Text>
          );
        },
      },
      {
        id: "source",
        header: "Source",
        accessorKey: "source",
        cell: ({ row }) => {
          return (
            <Flex align="center" gap="2">
              {getSourceIcon(row.original.source)}
              <Text>
                {row.original.source.charAt(0).toUpperCase() +
                  row.original.source.slice(1)}
              </Text>
            </Flex>
          );
        },
      },
      {
        id: "createdAt",
        header: "Created At",
        accessorKey: "createdAt",
        cell: ({ row }) => {
          return (
            <Text>
              <DateFormatter value={row.original.createdAt} />
            </Text>
          );
        },
      },
      {
        id: "updatedAt",
        header: "Updated At",
        accessorKey: "updatedAt",
        cell: ({ row }) => {
          return <DateFormatter value={row.original.updatedAt} />;
        },
      },
    ];

    return allColumns.filter((column) =>
      visibleColumns.includes(column.id as ViewColumn)
    );
  }, [visibleColumns, getSourceIcon]);

  if (viewsLoading) {
    return (
      <PageLayoutV2>
        <Flex width="100%" height="100dvh" align="center" justify="center">
          <Text>Loading views...</Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  if (!views || views.length === 0) {
    return (
      <PageLayoutV2>
        <Flex direction="column">
          <PageHeader
            title="Views"
            description="Create and manage custom views for your explore data"
            icon={
              <AnalyticsUpIcon
                size={24}
                fill={PageColors[ENavigate.EXPLORE_VIEWS] || "gray"}
              />
            }
            actions={
              <Button onClick={handleCreateView} highContrast>
                <Plus size={16} />
                Create View
              </Button>
            }
          />
          <Section size="4" pt="0">
            <Flex
              p="6"
              direction="column"
              width="100%"
              height="50dvh"
              align="center"
              justify="center"
              gap="4"
            >
              <Text size="5" weight="bold">
                No views yet
              </Text>
              <Text size="2" color="gray">
                Create your first view to get started
              </Text>
              <Button onClick={handleCreateView} highContrast>
                <Plus size={16} />
                Create View
              </Button>
            </Flex>
          </Section>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Views"
          description="Create and manage custom views for your explore data"
          icon={
            <AnalyticsUpIcon
              size={24}
              fill={PageColors[ENavigate.EXPLORE_VIEWS] || "gray"}
            />
          }
          actions={
            <Flex gap="2" align="center">
              <TextField.Root
                placeholder="Search views"
                size="2"
                onChange={(e) => setSearch(e.target.value)}
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
              <ExploreViewsDisplayOptions
                visibleColumns={visibleColumns}
                onChange={setVisibleColumns}
              />
              <Button onClick={handleCreateView} highContrast size="2">
                <Plus size={16} />
                New View
              </Button>
            </Flex>
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <SimpleTable
              data={sortedAndFilteredViews}
              onRowClicked={(row) => navigate(`/explore/views/${row.slug}`)}
              columns={tableColumns}
              loading={false}
              defaultSorting={sorting}
            />
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}
